import React from 'react';

import { DistanceNumber, Tooltip } from '@dndbeyond/character-common-components/es';
import {
    BaseInventoryName,
    NoteComponents,
    SignedNumberDice,
    SpellDamageEffect,
    SpellName,
    DiceComponentUtils,
} from '@dndbeyond/character-components/es';
import {
    AbilityLookup,
    ActivationUtils,
    BaseInventoryContract,
    CharacterTheme,
    ClassUtils,
    Constants,
    DataOriginRefData,
    EntityUtils,
    FormatUtils,
    Hack__BaseCharClass,
    ItemUtils,
    ScaledSpell,
    LimitedUseUtils,
    RuleData,
    Spell,
    SpellCasterInfo,
    SpellUtils,
} from '@dndbeyond/character-rules-engine/es';
import { DiceTools, RollType, RollRequest, Dice, DiceEvent, IRollContext } from '@dndbeyond/dice';
import { GameLogContext } from '@dndbeyond/game-log-components';

import SpellSlotChooser from '../SpellSlotChooser';
import { ThemeButton } from '../common/Button';

interface Props {
    className: string;
    spell: ScaledSpell;
    castLevel: number;
    characterLevel: number;
    isSpellSlotAvailable: boolean;
    isPactSlotAvailable: boolean;
    doesSpellSlotExist: boolean;
    doesPactSlotExist: boolean;
    ruleData: RuleData;
    abilityLookup: AbilityLookup;
    spellCasterInfo: SpellCasterInfo;
    dataOriginRefData: DataOriginRefData;
    onClick?: (spell: Spell, castLevel: number) => void;
    onUse?: (
        useSpellSlot: boolean,
        usePactMagicSlot: boolean,
        dataOriginType: Constants.DataOriginTypeEnum,
        uses: number | null,
        mappingId: number | null,
        mappingTypeId: number | null,
    ) => void;
    showNotes: boolean;
    isInteractive: boolean;
    diceEnabled: boolean;
    theme: CharacterTheme;
    proficiencyBonus: number;
    rollContext: IRollContext;
}
interface State {
    isSlotChooserOpen: boolean;
    isCriticalHit: boolean;
}
class SpellsSpell extends React.PureComponent<Props, State> {
    diceEventHandler: (eventData: any) => void;

    static defaultProps = {
        damage: '--',
        className: '',
        showNotes: true,
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            isSlotChooserOpen: false,
            isCriticalHit: false,
        };
    }

    componentDidMount() {
        this.diceEventHandler = DiceComponentUtils.setupResetCritStateOnRoll(
            SpellUtils.getName(this.props.spell),
            this,
        );
    }

    componentWillUnmount() {
        const { isSlotChooserOpen } = this.state;

        if (isSlotChooserOpen) {
            document.removeEventListener('click', this.handleDocumentClick);
        }

        Dice.removeEventListener(DiceEvent.ROLL, this.diceEventHandler);
    }

    handleSpellClick = (evt: React.MouseEvent): void => {
        const { onClick, spell, castLevel } = this.props;

        evt.nativeEvent.stopImmediatePropagation();
        evt.stopPropagation();

        if (onClick) {
            onClick(spell, castLevel);
        }
    };

    handleDisabledCastClick = (): void => {
        const { onClick, spell, castLevel } = this.props;

        if (onClick) {
            onClick(spell, castLevel);
        }
    };

    handleSpellUse = (useSpellSlot: boolean, usePactMagicSlot: boolean): void => {
        const { onUse, spell } = this.props;

        let dataOrigin = SpellUtils.getDataOrigin(spell);
        let dataOriginType = SpellUtils.getDataOriginType(spell);
        let limitedUse = SpellUtils.getLimitedUse(spell);
        let uses: number | null = null;
        let mappingId: number | null = null;
        let mappingTypeId: number | null = null;

        if (limitedUse) {
            let numberUsed = LimitedUseUtils.getNumberUsed(limitedUse);
            let minConsumed = LimitedUseUtils.getMinNumberConsumed(limitedUse);
            let consumedAmount: number = minConsumed + SpellUtils.getScaledAmount(spell);

            if (dataOriginType === Constants.DataOriginTypeEnum.ITEM) {
                uses = numberUsed + consumedAmount;
                mappingId = ItemUtils.getMappingId(dataOrigin.primary as BaseInventoryContract);
                mappingTypeId = ItemUtils.getMappingEntityTypeId(dataOrigin.primary as BaseInventoryContract);
            } else {
                uses = numberUsed + 1;
                mappingId = SpellUtils.getMappingId(spell);
                mappingTypeId = SpellUtils.getMappingEntityTypeId(spell);
            }
        }

        if (onUse) {
            onUse(useSpellSlot, usePactMagicSlot, dataOriginType, uses, mappingId, mappingTypeId);
        }
    };

    handleSpellSlotChooserOpen = (): void => {
        this.setState({
            isSlotChooserOpen: true,
        });
        document.addEventListener('click', this.handleDocumentClick);
    };

    handleSpellSlotChooserClose = (): void => {
        this.setState({
            isSlotChooserOpen: false,
        });
    };

    handleDocumentClick = (): void => {
        this.handleSpellSlotChooserClose();
        document.removeEventListener('click', this.handleDocumentClick);
    };

    handleSpellSlotChosen = (): void => {
        this.handleSpellUse(true, false);
        this.setState({
            isSlotChooserOpen: false,
        });
    };

    handlePactSlotChosen = (): void => {
        this.handleSpellUse(false, true);
        this.setState({
            isSlotChooserOpen: false,
        });
    };

    handleCastClick = (): void => {
        const { spell, isSpellSlotAvailable, isPactSlotAvailable, doesSpellSlotExist, doesPactSlotExist } = this.props;

        let usesSpellSlot = SpellUtils.getUsesSpellSlot(spell);
        let limitedUse = SpellUtils.getLimitedUse(spell);
        if (usesSpellSlot) {
            if (limitedUse) {
                const spellDataOrigin = SpellUtils.getDataOrigin(spell);
                const spellDataOriginType = SpellUtils.getDataOriginType(spell);
                if (
                    spellDataOriginType === Constants.DataOriginTypeEnum.CLASS_FEATURE &&
                    ClassUtils.isPactMagicActive(spellDataOrigin.parent as Hack__BaseCharClass)
                ) {
                    if (isPactSlotAvailable) {
                        this.handleSpellUse(false, true);
                    }
                } else {
                    if (isSpellSlotAvailable) {
                        this.handleSpellUse(true, false);
                    }
                }
            } else {
                if (doesSpellSlotExist && doesPactSlotExist) {
                    this.handleSpellSlotChooserOpen();
                } else {
                    if (isSpellSlotAvailable) {
                        this.handleSpellUse(true, false);
                    } else if (isPactSlotAvailable) {
                        this.handleSpellUse(false, true);
                    }
                }
            }
        } else {
            this.handleSpellUse(false, false);
        }
    };

    handleRollResults = (result: RollRequest): void => {
        let wasCrit = DiceComponentUtils.isCriticalRoll(result);
        this.setState({ isCriticalHit: wasCrit });
    };

    renderAttackInfo = (): React.ReactNode => {
        const { spell, ruleData, diceEnabled, theme, rollContext } = this.props;

        const [{ messageTargetOptions, defaultMessageTargetOption, userId }] = this.context;

        let toHit: number | null = null;
        let saveDcValue: number | null = null;
        let saveDcLabel: string | null = null;
        let asPartOfWeaponAttack = SpellUtils.asPartOfWeaponAttack(spell);
        let requiresAttackRoll = SpellUtils.getRequiresAttackRoll(spell);
        let requiresSavingThrow = SpellUtils.getRequiresSavingThrow(spell);
        if (requiresAttackRoll) {
            toHit = SpellUtils.getToHit(spell);
        } else if (requiresSavingThrow) {
            saveDcValue = SpellUtils.getAttackSaveValue(spell);
            saveDcLabel = SpellUtils.getSaveDcAbilityKey(spell, ruleData);
        }

        if (!asPartOfWeaponAttack) {
            if (requiresAttackRoll && toHit !== null) {
                return (
                    <div className="ct-spells-spell__tohit">
                        <SignedNumberDice
                            number={toHit}
                            diceNotation={DiceTools.CustomD20(toHit)}
                            rollType={RollType.ToHit}
                            rollAction={SpellUtils.getName(spell)}
                            diceEnabled={diceEnabled}
                            advMenu={true}
                            themeColor={theme.themeColor}
                            onRollResults={this.handleRollResults}
                            rollContext={rollContext}
                            rollTargetOptions={
                                messageTargetOptions ? Object.values(messageTargetOptions?.entities) : undefined
                            }
                            rollTargetDefault={defaultMessageTargetOption}
                            userId={userId}
                        />
                    </div>
                );
            } else if (requiresSavingThrow) {
                return (
                    <div className="ct-spells-spell__save">
                        <span
                            className={`ct-spells-spell__save-label ${
                                theme?.isDarkMode ? 'ct-spells-spell--dark-mode' : ''
                            }`}
                        >
                            {saveDcLabel}
                        </span>
                        <span
                            className={`ct-spells-spell__save-value ${
                                theme?.isDarkMode ? 'ct-spells-spell--dark-mode' : ''
                            }`}
                        >
                            {saveDcValue}
                        </span>
                    </div>
                );
            }
        }

        return <div className="ct-spells-spell__empty-value">--</div>;
    };

    render() {
        const { isSlotChooserOpen, isCriticalHit } = this.state;
        const {
            spell,
            className,
            abilityLookup,
            ruleData,
            isSpellSlotAvailable,
            isPactSlotAvailable,
            doesSpellSlotExist,
            doesPactSlotExist,
            castLevel,
            characterLevel,
            showNotes,
            isInteractive,
            diceEnabled,
            theme,
            dataOriginRefData,
            proficiencyBonus,
            rollContext,
        } = this.props;

        let range = SpellUtils.getRange(spell);
        let limitedUse = SpellUtils.getLimitedUse(spell);
        let activation = SpellUtils.getActivation(spell);
        let spellScaledAmount = SpellUtils.getScaledAmount(spell);
        let isScaled = SpellUtils.getCastLevel(spell) !== SpellUtils.getLevel(spell);
        let isAtWill = SpellUtils.isAtWill(spell);
        const spellDataOriginType = SpellUtils.getDataOriginType(spell);
        const spellDataOrigin = SpellUtils.getDataOrigin(spell);

        let limitedUseButtonText: React.ReactNode;
        if (limitedUse) {
            let maxUses = SpellUtils.getMaxUses(spell);
            let consumedAmount = SpellUtils.getConsumedUses(spell);
            if (maxUses === 1 && spellDataOriginType !== Constants.DataOriginTypeEnum.ITEM) {
                limitedUseButtonText = 'Use';
            } else {
                limitedUseButtonText = `${consumedAmount} ${
                    spellDataOriginType === Constants.DataOriginTypeEnum.ITEM ? 'C' : 'U'
                }`;
            }
        }

        let usesSpellSlot = SpellUtils.getUsesSpellSlot(spell);

        let canCastSpell: boolean = true;
        if (limitedUse) {
            canCastSpell = SpellUtils.isLimitedUseAvailableAtScaledAmount(spell);
        } else if (usesSpellSlot) {
            canCastSpell = isSpellSlotAvailable || isPactSlotAvailable;
        }

        let scaledInfoNode: React.ReactNode;
        if (isScaled) {
            scaledInfoNode = (
                <span className="ct-spells-spell__scaled">
                    <span className="ct-spells-spell__scaled-level">
                        <span className="ct-spells-spell__scaled-level-number">{SpellUtils.getLevel(spell)}</span>
                        <span className="ct-spells-spell__scaled-level-ordinal">
                            {FormatUtils.getOrdinalSuffix(SpellUtils.getLevel(spell))}
                        </span>
                    </span>
                </span>
            );
        }

        let combinedMetaItems: Array<React.ReactNode> = [];
        switch (spellDataOriginType) {
            case Constants.DataOriginTypeEnum.ITEM:
                combinedMetaItems.push(
                    <BaseInventoryName
                        item={spellDataOrigin.primary as BaseInventoryContract}
                        showIcons={false}
                        theme={theme}
                    />,
                );
                break;
            default:
                combinedMetaItems.push(EntityUtils.getDataOriginName(spellDataOrigin));
        }

        let expandedDataOriginRef = SpellUtils.getExpandedDataOriginRef(spell);
        if (expandedDataOriginRef !== null) {
            combinedMetaItems.push(EntityUtils.getDataOriginRefName(expandedDataOriginRef, dataOriginRefData));
        }

        let castAsRitual = SpellUtils.isCastAsRitual(spell);

        let actionNode: React.ReactNode;
        if (castAsRitual) {
            actionNode = <span className="ct-spells-spell__as-ritual">As Ritual</span>;
        } else if (isAtWill) {
            actionNode = <span className="ct-spells-spell__at-will">At Will</span>;
        } else {
            actionNode = (
                <React.Fragment>
                    <ThemeButton
                        size={'small'}
                        onClick={canCastSpell ? this.handleCastClick : this.handleDisabledCastClick}
                        disabled={!canCastSpell}
                        block={true}
                        isInteractive={isInteractive}
                    >
                        {scaledInfoNode}
                        {limitedUse ? limitedUseButtonText : 'Cast'}
                    </ThemeButton>
                    {isSlotChooserOpen && canCastSpell && (
                        <SpellSlotChooser
                            onPactChoose={this.handlePactSlotChosen}
                            onSpellChoose={this.handleSpellSlotChosen}
                            isSpellSlotAvailable={isSpellSlotAvailable}
                            isPactSlotAvailable={isPactSlotAvailable}
                            doesSpellSlotExist={doesSpellSlotExist}
                            doesPactSlotExist={doesPactSlotExist}
                        />
                    )}
                </React.Fragment>
            );
        }
        let classNames: Array<string> = ['ct-spells-spell', className];
        if (isCriticalHit) {
            classNames.push('ct-spells-spell--crit');
        }
        return (
            <div className={classNames.join(' ')} onClick={this.handleSpellClick}>
                <div className="ct-spells-spell__action">{actionNode}</div>
                <div className="ct-spells-spell__name">
                    <div className={`ct-spells-spell__label ${isScaled ? 'ct-spells-spell__label--scaled' : ''}`}>
                        <SpellName
                            spell={spell}
                            showSpellLevel={false}
                            dataOriginRefData={dataOriginRefData}
                            theme={theme}
                        />
                    </div>
                    {combinedMetaItems.length > 0 && (
                        <div
                            className={`ct-spells-spell__meta ${
                                theme.isDarkMode ? 'ct-spells-spell__meta--dark-mode' : ''
                            }`}
                        >
                            {combinedMetaItems.map((metaItem, idx) => (
                                <span className="ct-spells-spell__meta-item" key={idx}>
                                    {metaItem}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {activation !== null && (
                    <div
                        className={`ct-spells-spell__activation ${
                            theme.isDarkMode ? 'ct-spells-spell__activation--dark-mode' : ''
                        }`}
                    >
                        <Tooltip
                            isDarkMode={theme.isDarkMode}
                            title={ActivationUtils.renderCastingTime(activation, castAsRitual ? 10 : 0, ruleData)}
                            tippyOpts={{ dynamicTitle: true }}
                        >
                            {ActivationUtils.renderCastingTimeAbbreviation(activation)}
                            {castAsRitual && <span className="ct-spells-spell__activation-extra">+10m</span>}
                        </Tooltip>
                    </div>
                )}
                {range !== null && (
                    <div className={`ct-spells-spell__range ${theme?.isDarkMode ? 'ct-spells-spell--dark-mode' : ''}`}>
                        {!!range.origin && range.origin !== Constants.SpellRangeTypeNameEnum.RANGED && (
                            <span className="ct-spells-spell__range-origin">{range.origin}</span>
                        )}
                        {!!range.rangeValue && (
                            <span className="ct-spells-spell__range-value">
                                <DistanceNumber number={range.rangeValue} isDarkMode={theme.isDarkMode} />
                            </span>
                        )}
                    </div>
                )}
                <div className="ct-spells-spell__attacking">{this.renderAttackInfo()}</div>
                <div className="ct-spells-spell__damage">
                    <SpellDamageEffect
                        spell={spell}
                        characterLevel={characterLevel}
                        castLevel={castLevel}
                        ruleData={ruleData}
                        diceEnabled={diceEnabled}
                        theme={theme}
                        isCriticalHit={isCriticalHit}
                        rollContext={rollContext}
                    />
                </div>
                {showNotes && (
                    <div className="ct-spells-spell__notes">
                        <NoteComponents
                            theme={theme}
                            notes={SpellUtils.getNoteComponents(
                                spell,
                                castLevel,
                                characterLevel,
                                abilityLookup,
                                ruleData,
                                spellScaledAmount,
                                proficiencyBonus,
                            )}
                        />
                    </div>
                )}
            </div>
        );
    }
}

SpellsSpell.contextType = GameLogContext;

export default SpellsSpell;
